import { Component, inject, signal } from '@angular/core';
import { Auth, reload, sendEmailVerification, user } from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [],
  templateUrl: './verify-email.component.html',
})
export class VerifyEmailComponent {
  private auth        = inject(Auth);
  private router      = inject(Router);
  private route       = inject(ActivatedRoute);
  private userService = inject(UserService);

  private get returnUrl(): string | undefined {
    return this.route.snapshot.queryParamMap.get('returnUrl') ?? undefined;
  }

  readonly authUser  = toSignal(user(this.auth));
  readonly checking  = signal(false);
  readonly resending = signal(false);
  readonly resent    = signal(false);
  readonly error     = signal('');

  /** Reloads the Firebase user token and redirects if email is now verified. */
  async checkVerification(): Promise<void> {
    const current = this.auth.currentUser;
    if (!current) { this.router.navigate(['/login']); return; }

    this.checking.set(true);
    this.error.set('');
    try {
      await reload(current);
      if (this.auth.currentUser?.emailVerified) {
        // Create the Firestore user profile now that the email is confirmed.
        const u = this.auth.currentUser;
        await this.userService.ensureUserProfile(u.uid, u.email, u.displayName, u.photoURL, 'owner');
        this.router.navigateByUrl(this.returnUrl ?? '/dashboard');
      } else {
        this.error.set('Todavía no hemos detectado la verificación. Revisa tu bandeja de entrada y haz clic en el enlace.');
      }
    } catch {
      this.error.set('Ocurrió un error al comprobar el estado. Inténtalo de nuevo.');
    } finally {
      this.checking.set(false);
    }
  }

  /** Resends the verification email to the current user. */
  async resendVerification(): Promise<void> {
    const current = this.auth.currentUser;
    if (!current) { this.router.navigate(['/login']); return; }

    this.resending.set(true);
    this.error.set('');
    try {
      await sendEmailVerification(current);
      this.resent.set(true);
    } catch {
      this.error.set('No se pudo reenviar el correo. Espera unos minutos e inténtalo de nuevo.');
    } finally {
      this.resending.set(false);
    }
  }

  signOut(): void {
    this.auth.signOut().then(() => this.router.navigate(['/login']));
  }
}
